import { Link, useLoaderData } from 'react-router';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Separator,
  StatusBadge,
} from '@repo-manager/ui';

import { getActiveRepoId } from '../lib/repo-context';
import { getSupabase } from '../lib/supabase';

interface RoadmapRow {
  repo: { id: string; name: string; owner: string } | null;
  vision: { content: string; version: number } | null;
  goals: { id: string; description: string; status: string }[];
  milestones: { id: string; title: string; goal_id: string | null; status: string; target_date: string | null }[];
  features: {
    id: string;
    title: string;
    narrative: string;
    status: string;
    priority: number;
    milestone_id: string | null;
  }[];
}

export async function roadmapLoader(): Promise<RoadmapRow> {
  const supabase = getSupabase();
  const repoId = await getActiveRepoId(supabase);
  if (!repoId) {
    return { repo: null, vision: null, goals: [], milestones: [], features: [] };
  }
  const { data: repoRow } = await supabase
    .from('repos')
    .select('id, name, owner')
    .eq('id', repoId)
    .maybeSingle();
  const repo = repoRow ?? null;
  if (!repo) {
    return { repo: null, vision: null, goals: [], milestones: [], features: [] };
  }
  const [visionR, goalsR, milestonesR, featuresR] = await Promise.all([
    supabase.from('vision').select('content, version').eq('repo_id', repo.id).maybeSingle(),
    supabase.from('goals').select('id, description, status').eq('repo_id', repo.id),
    supabase
      .from('milestones')
      .select('id, title, goal_id, status, target_date')
      .eq('repo_id', repo.id),
    supabase
      .from('features')
      .select('id, title, narrative, status, priority, milestone_id')
      .eq('repo_id', repo.id)
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false }),
  ]);
  return {
    repo,
    vision: visionR.data ?? null,
    goals: goalsR.data ?? [],
    milestones: milestonesR.data ?? [],
    features: featuresR.data ?? [],
  };
}

export function RoadmapPage() {
  const data = useLoaderData() as RoadmapRow;

  if (!data.repo) {
    return (
      <EmptyState
        title="No repo connected"
        description="Insert a row in `public.repos` to start tracking a roadmap."
      />
    );
  }

  const unscopedFeatures = data.features.filter((f) => f.milestone_id == null);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{data.repo.owner}/{data.repo.name}</h1>
          {data.vision ? (
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{data.vision.content}</p>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground italic">
              No vision set yet (insert one into `public.vision`).
            </p>
          )}
        </div>
        <Button asChild>
          <Link to="/features/new">+ New feature</Link>
        </Button>
      </div>

      <Separator />

      {data.goals.length === 0 && data.features.length === 0 ? (
        <EmptyState
          title="No goals or features yet"
          description="Add a goal, then milestones and features will tree under it."
        />
      ) : null}

      {data.goals.map((goal) => {
        const goalMilestones = data.milestones.filter((m) => m.goal_id === goal.id);
        return (
          <Card key={goal.id}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-lg">
                <span>{goal.description}</span>
                <StatusBadge status={goal.status} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {goalMilestones.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No milestones under this goal.</p>
              ) : (
                goalMilestones.map((m) => {
                  const ms = data.features.filter((f) => f.milestone_id === m.id);
                  return (
                    <div key={m.id} className="rounded-md border border-border p-4">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{m.title}</div>
                        <StatusBadge status={m.status} />
                      </div>
                      <ul className="mt-2 divide-y divide-border">
                        {ms.map((f) => (
                          <li key={f.id} className="flex items-center justify-between py-2 text-sm">
                            <Link to={`/features/${f.id}`} className="hover:underline">
                              {f.title}
                            </Link>
                            <StatusBadge status={f.status} />
                          </li>
                        ))}
                        {ms.length === 0 ? (
                          <li className="py-2 text-sm italic text-muted-foreground">No features.</li>
                        ) : null}
                      </ul>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        );
      })}

      {unscopedFeatures.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Unscoped features</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {unscopedFeatures.map((f) => (
                <li key={f.id} className="flex items-center justify-between py-2 text-sm">
                  <Link to={`/features/${f.id}`} className="hover:underline">
                    {f.title}
                  </Link>
                  <StatusBadge status={f.status} />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
